/**
 * @voltxampere/presets — RC low-pass filter (#2)
 *
 * V1 (DC) → R → out → C → gnd. Cap's negative terminal sits at
 * ground so the engine honours `initialVoltage = 0` (Sprint 6
 * floating-cap IC limitation does not apply). Transient run-once
 * with τ = RC = 1 ms charges V(out) from 0 toward V1; at one time
 * constant V(out) = V1·(1 − e⁻¹) ≈ 0.632 V.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
// Sprint 25 R2 lane 4D-A — hand-authored layout. Source on the
// left, series resistor in the middle, shunt capacitor on the
// right. Cap rotated 90° so its top terminal meets the horizontal
// "out" rail and its bottom terminal drops to ground.
export const rcLowPass = {
    id: "rc-low-pass",
    name: { tr: "RC Alçak Geçiren Filtre", en: "RC Low-Pass Filter" },
    category: "basic",
    difficulty: 2,
    description: {
        tr: "Birinci dereceden alçak geçiren filtre. R=1kΩ, C=1µF, τ=1ms.",
        en: "First-order low-pass filter. R=1kΩ, C=1µF, τ=1ms.",
    },
    circuit: makeCircuit({
        slot: 2,
        name: "RC Low-Pass",
        description: "First-order RC low-pass filter, τ = 1 ms",
        defaultAnalysis: "transient",
        transient: { tstop: 5e-3 },
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_in", p2: "net_out" },
                label: "R1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 1e-6, initialVoltage: 0 },
                terminals: { p1: "net_out", p2: "net_gnd" },
                label: "C1",
                position: { x: 160, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // in rail: V1.pos (-160, -10) → R1.p1 (-20, 0)
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -40 },
                    { x: -20, y: -40 },
                    { x: -20, y: 0 },
                ],
                style: "orthogonal",
            },
            // out rail: R1.p2 (20, 0) → C1.p1 (160, -20)
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 20, y: 0 },
                    { x: 160, y: 0 },
                    { x: 160, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd leg A: V1.neg (-160, 10) → GND.p (-160, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd leg B: C1.p2 (160, 20) → GND.p (-160, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 160, y: 20 },
                    { x: 160, y: 110 },
                    { x: -160, y: 110 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "transient",
            time: 1e-3,
            value: 0.632,
            tolerance: 0.05,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Birinci dereceden geçici cevap: V(t) = V∞·(1 − e^(−t/τ)).",
            en: "First-order step response: V(t) = V∞·(1 − e^(−t/τ)).",
        },
        {
            tr: "Zaman sabiti τ = RC ile yükselme süresi arasındaki ilişki.",
            en: "Time constant τ = RC and its relation to rise time.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=rc-low-pass.js.map